// pp-factura-modal.js — Modal de facturación CFDI desde el perfil del cliente.
//
// Perfiles y emisión van por el App Proxy AUTENTICADO (/apps/crm/customer-fiscal
// para listar, /apps/crm/customer-factura para emitir): Shopify firma con HMAC +
// logged_in_customer_id, así que la pertenencia se valida a NIVEL DE CUENTA por la
// identidad del cliente (shopify_customer_id → cuenta CRM), no por su correo. Así
// cualquier miembro de la empresa factura cualquier orden de la cuenta con cualquier
// perfil de la cuenta (antes fallaba con "Estos datos fiscales no corresponden a esta
// orden" cuando la orden y el perfil caían en cuentas CRM distintas).
//
// lookup-orden y descargar siguen por el edge público `facturacion-publica` (no
// exponen dato fiscal sensible). En el perfil ya conocemos orden + total, así que el
// flujo es: abrir → resumen inmediato desde Liquid (data-attrs del trigger) +
// perfiles y lookup-orden EN PARALELO e independientes (#3383) → elegir perfil
// fiscal guardado → emitir → descargar. Cliente logueado ⇒ 2 clics. Emitir solo
// se habilita con lookup exitoso (order_id real) + perfil elegido.
//
// callFact / downloadInvoice / formatMoney se DUPLICAN a propósito del wizard:
// el wizard es un IIFE acoplado a su DOM (step1/2/3) y refactorizarlo a módulo
// compartido en producción es riesgo innecesario para Fase 1. Deuda registrada
// en docs/facturacion-perfil-cliente.md (extraer a pp-fact-core.js).
//
// Contratos: docs/facturacion-perfil-cliente.md.

(function () {
  'use strict';

  var FACT_BASE = 'https://dhygbnysealeblqorjts.supabase.co/functions/v1/facturacion-publica';
  // publishable key pública por diseño (verify_jwt=false; la autorización es de
  // negocio: order_number+total+ownership). Idéntica a pp-facturacion-publica.js.
  var FACT_APIKEY = 'sb_publishable_48y6S7h2eQ-9uTF7pCNJNQ_Q0mz8VVX';

  var I18N = window.PP_FACT_I18N || {};

  var modal = document.querySelector('[data-pp-fact-modal]');
  if (!modal || typeof modal.showModal !== 'function') return;

  // ── Refs ───────────────────────────────────────────────────────
  var titleEl = modal.querySelector('[data-pp-fact-title]');
  var loadingEl = modal.querySelector('[data-pp-fact-loading]');
  var bodyEl = modal.querySelector('[data-pp-fact-body]');
  var metaEl = modal.querySelector('[data-pp-fact-summary-meta]');
  var tableEl = modal.querySelector('[data-pp-fact-summary-table]');
  var profilesSection = modal.querySelector('[data-pp-fact-profiles-section]');
  var profilesEl = modal.querySelector('[data-pp-fact-profiles]');
  var noprofileEl = modal.querySelector('[data-pp-fact-noprofile]');
  var profilesErrorEl = modal.querySelector('[data-pp-fact-profiles-error]');
  var noticeEl = modal.querySelector('[data-pp-fact-notice]');
  var paymentEl = modal.querySelector('[data-pp-fact-payment]');
  var errorEl = modal.querySelector('[data-pp-fact-error]');
  var successEl = modal.querySelector('[data-pp-fact-success]');
  var footEl = modal.querySelector('[data-pp-fact-foot]');
  var emitBtn = modal.querySelector('[data-pp-fact-emit]');

  // ── Estado ─────────────────────────────────────────────────────
  var order = null;            // objeto de lookup-orden (solo lookup EXITOSO)
  var selectedProfile = null;  // perfil fiscal elegido
  var lastTrigger = null;      // botón que abrió (para devolver foco)
  // lookupState gobierna el gate de Emitir: solo 'ok' permite emitir.
  // 'idle' | 'loading' | 'ok' | 'not_paid' | 'processing' | 'blocked' | 'error'
  var lookupState = 'idle';
  // openSeq invalida callbacks (lookup con retry, timeouts) de aperturas viejas
  // cuando el cliente cierra y reabre el modal con otra orden.
  var openSeq = 0;

  // Reintentos del lookup ante 404: el webhook de sync tarda ~8s-60s en órdenes
  // recién creadas (#3383). 3 intentos totales: 0s, 5s, 15s acumulados.
  var LOOKUP_RETRY_DELAYS = [5000, 10000];

  // ── Helpers (duplicados del wizard, código estable) ────────────
  function callFact(path, body, method) {
    method = method || 'POST';
    var opts = {
      method: method,
      headers: {
        'Authorization': 'Bearer ' + FACT_APIKEY,
        'apikey': FACT_APIKEY,
        'Content-Type': 'application/json'
      }
    };
    if (body != null) opts.body = JSON.stringify(body);
    return fetch(FACT_BASE + '/' + path, opts)
      .then(function (res) { return res.json().catch(function () { return null; }); })
      .then(function (json) {
        if (!json) return { ok: false, error: I18N.error_generic, code: 'invalid_response' };
        return json;
      })
      .catch(function (err) {
        console.error('[factura-modal] red:', err);
        return { ok: false, error: I18N.error_generic, code: 'network_error' };
      });
  }

  // Llama al App Proxy AUTENTICADO (/apps/crm/*). Shopify firma la request con HMAC +
  // logged_in_customer_id, así que el backend conoce la identidad real del cliente
  // logueado (a diferencia de callFact, que pega al edge público con apikey). Se usa
  // para listar perfiles (GET customer-fiscal) y emitir (POST customer-factura), donde
  // la pertenencia se resuelve a nivel de cuenta por la identidad de Shopify.
  function callProxy(path, method, body) {
    var opts = { method: method || 'GET', credentials: 'same-origin', cache: 'no-store' };
    if (body != null) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(body);
    }
    return fetch('/apps/crm/' + path, opts)
      .then(function (res) {
        return res.json().catch(function () { return null; }).then(function (json) {
          // El shape {ok,...} del edge trae su propio veredicto — se respeta.
          if (json && typeof json === 'object' && 'ok' in json) return json;
          // Para shapes sin `ok` (GET customer-fiscal: {found, profiles}) el
          // status HTTP es la única señal de error: un 500 también trae
          // profiles:[] en el body y NO significa "cero perfiles" (#3383).
          if (!res.ok || !json) {
            return { ok: false, error: I18N.error_generic, code: res.ok ? 'invalid_response' : 'http_' + res.status };
          }
          return json;
        });
      })
      .catch(function (err) {
        console.error('[factura-modal] proxy:', err);
        return { ok: false, error: I18N.error_generic, code: 'network_error' };
      });
  }

  function formatMoney(n) {
    var amount = Number(n) || 0;
    return '$' + amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MXN';
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (_) { return ''; }
  }

  function clearChildren(el) { while (el && el.firstChild) el.removeChild(el.firstChild); }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg || I18N.error_generic;
    errorEl.hidden = false;
    errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  function hideError() { if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; } }

  function downloadInvoice(facturaId, tipo) {
    callFact('descargar/' + facturaId + '/' + tipo, null, 'GET').then(function (result) {
      if (!result.ok || !result.data || !result.data.url) {
        showError(result.error || I18N.error_generic);
        return;
      }
      var url = result.data.url;
      var folio = result.data.folio || facturaId;
      var filename = 'factura-' + folio + '.' + tipo;
      fetch(url)
        .then(function (res) { if (!res.ok) throw new Error('http_' + res.status); return res.blob(); })
        .then(function (blob) {
          var blobUrl = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = blobUrl; a.download = filename; a.style.display = 'none';
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          setTimeout(function () { URL.revokeObjectURL(blobUrl); }, 100);
        })
        .catch(function (err) { console.error('[factura-modal] download', err); showError(I18N.error_generic); });
    });
  }

  // ── Fases de UI ────────────────────────────────────────────────
  function setPhase(phase) {
    if (loadingEl) loadingEl.hidden = phase !== 'loading';
    if (bodyEl) bodyEl.hidden = phase !== 'body';
    if (successEl) successEl.hidden = phase !== 'success';
    if (footEl) footEl.hidden = phase !== 'body';
  }

  function resetModal() {
    order = null;
    selectedProfile = null;
    lookupState = 'idle';
    hideError();
    hideNotice();
    clearChildren(metaEl);
    clearChildren(tableEl);
    clearChildren(profilesEl);
    clearChildren(paymentEl);
    clearChildren(successEl);
    if (paymentEl) paymentEl.hidden = true;
    if (profilesSection) profilesSection.hidden = true;
    if (noprofileEl) noprofileEl.hidden = true;
    if (profilesErrorEl) profilesErrorEl.hidden = true;
    if (emitBtn) { emitBtn.disabled = true; emitBtn.textContent = I18N.emit || 'Emitir factura'; }
  }

  // ── Aviso de estado (sobre el modal completo y funcional) ──────
  function showNotice(kind, msg, withSpinner) {
    if (!noticeEl) return;
    clearChildren(noticeEl);
    noticeEl.className = 'pp-fact-modal__notice pp-fact-modal__notice--' + kind;
    if (withSpinner) {
      var sp = document.createElement('span');
      sp.className = 'pp-fact-modal__spinner pp-fact-modal__spinner--sm';
      sp.setAttribute('aria-hidden', 'true');
      noticeEl.appendChild(sp);
    }
    noticeEl.appendChild(document.createTextNode(msg || ''));
    noticeEl.hidden = false;
  }
  function hideNotice() {
    if (noticeEl) { noticeEl.hidden = true; clearChildren(noticeEl); }
  }

  // Gate de Emitir: requiere lookup exitoso (order_id real) + perfil elegido.
  function updateEmit() {
    if (emitBtn) emitBtn.disabled = !(lookupState === 'ok' && order && selectedProfile);
  }

  // ── Abrir: resumen inmediato desde Liquid + perfiles y lookup en paralelo ──
  //
  // Los datos de la orden (productos, cantidades, importes) ya viven en el
  // Liquid de Mis Órdenes: el trigger los trae en data-attrs y el resumen se
  // renderiza AL INSTANTE, sin depender del lookup (#3383). El lookup después
  // lo re-renderiza con los datos post-refund del backend cuando responde.
  function readTriggerOrder(trigger, orderNumber, totalPesos) {
    if (!trigger || !trigger.getAttribute('data-order-items')) return null;
    var items = [];
    try { items = JSON.parse(trigger.getAttribute('data-order-items')); } catch (_) { items = []; }
    return {
      order_number: orderNumber,
      total: totalPesos,
      shopify_created_at: trigger.getAttribute('data-order-date') || null,
      line_items: Array.isArray(items) ? items : []
    };
  }

  function openModal(orderNumber, totalPesos, trigger) {
    lastTrigger = trigger || null;
    openSeq++;
    var seq = openSeq;
    resetModal();
    if (titleEl) titleEl.textContent = (I18N.title || 'Facturar') + ' ' + orderNumber;
    modal.showModal();

    var liquidOrder = readTriggerOrder(trigger, orderNumber, totalPesos);
    if (liquidOrder) renderSummary(liquidOrder);
    setPhase('body');
    lookupState = 'loading';
    showNotice('loading', I18N.checking || 'Verificando tu orden…', true);

    // Perfiles SIEMPRE en paralelo, vía App Proxy autenticado — independientes
    // del resultado del lookup de la orden (#3383).
    loadProfiles();
    lookupAttempt(orderNumber, totalPesos, seq, 0);
  }

  function lookupAttempt(orderNumber, totalPesos, seq, attempt) {
    callFact('lookup-orden', { order_number: orderNumber, total: totalPesos }).then(function (result) {
      if (seq !== openSeq) return; // el modal se cerró o se reabrió con otra orden

      if (result.ok) {
        var data = result.data || {};
        if (data.can_invoice === false && data.reason === 'already_invoiced') {
          hideNotice();
          renderAlreadyInvoiced(data.factura);
          return;
        }
        order = data.order;
        if (!order) {
          lookupState = 'error';
          showNotice('error', I18N.error_generic);
          return;
        }
        lookupState = 'ok';
        hideNotice();
        renderSummary(order);
        applyPaymentForm(order);
        if (order.es_mes_anterior_en_ventana && order.mensaje_ventana) {
          showNotice('info', order.mensaje_ventana);
        }
        updateEmit();
        return;
      }

      // 404: en Mis Órdenes la orden EXISTE en Shopify (viene del Liquid) —
      // un not_found aquí es lag del webhook de sync, no un número inválido.
      // Reintentamos y, si se agotan los intentos, "se está procesando";
      // nunca "no encontramos esta orden".
      if (result.code === 'order_not_found') {
        showNotice('loading', I18N.processing || 'Tu orden se está procesando. Intenta de nuevo en un momento.', true);
        if (attempt < LOOKUP_RETRY_DELAYS.length) {
          setTimeout(function () {
            if (seq !== openSeq) return;
            lookupAttempt(orderNumber, totalPesos, seq, attempt + 1);
          }, LOOKUP_RETRY_DELAYS[attempt]);
        } else {
          lookupState = 'processing';
          showNotice('info', I18N.processing || 'Tu orden se está procesando. Intenta de nuevo en un momento.');
        }
        return;
      }

      // 422 no-pagada: mensaje claro sobre el modal funcional; el resumen del
      // backend (si viene) reemplaza al de Liquid. Emitir queda deshabilitado.
      if (result.code === 'not_paid') {
        lookupState = 'not_paid';
        if (result.order) renderSummary(result.order);
        showNotice('warn', I18N.pending_payment || result.error || '');
        return;
      }

      // 422 con causa propia (fuera de ventana, total $0, saldo Envia):
      // el backend ya trae el mensaje correcto para el cliente.
      if (result.code === 'not_current_month' || result.code === 'total_cero' || result.code === 'saldo_envia') {
        lookupState = 'blocked';
        showNotice('info', result.error || I18N.error_generic);
        return;
      }

      lookupState = 'error';
      showNotice('error', result.error || I18N.error_generic);
    });
  }

  // ── Doble-check ────────────────────────────────────────────────
  function makeMeta(label, value) {
    var d = document.createElement('div');
    d.className = 'pp-fact-modal__meta-item';
    var l = document.createElement('span'); l.className = 'pp-fact-modal__meta-label'; l.textContent = label;
    var v = document.createElement('strong'); v.className = 'pp-fact-modal__meta-value'; v.textContent = value;
    d.appendChild(l); d.appendChild(v);
    return d;
  }

  function renderSummary(o) {
    clearChildren(metaEl);
    metaEl.appendChild(makeMeta(I18N.meta_number || 'Orden', o.order_number || ''));
    var dt = formatDate(o.shopify_created_at || o.created_at);
    if (dt) metaEl.appendChild(makeMeta(I18N.meta_date || 'Fecha', dt));
    metaEl.appendChild(makeMeta(I18N.total_label || 'Total', formatMoney(o.total)));

    clearChildren(tableEl);
    (o.line_items || []).forEach(function (li) {
      var tr = document.createElement('tr');
      var tdName = document.createElement('td');
      tdName.textContent = li.name || li.sku || '';
      var tdQty = document.createElement('td'); tdQty.className = 'pp-fact-modal__num';
      tdQty.textContent = String(li.quantity || 0);
      var tdTotal = document.createElement('td'); tdTotal.className = 'pp-fact-modal__num';
      tdTotal.textContent = formatMoney(li.line_total);
      tr.appendChild(tdName); tr.appendChild(tdQty); tr.appendChild(tdTotal);
      tableEl.appendChild(tr);
    });
  }

  // ── Forma de pago (solo si la orden lo requiere) ───────────────
  function applyPaymentForm(o) {
    clearChildren(paymentEl);
    if (!o.needs_payment_form) { paymentEl.hidden = true; return; }
    paymentEl.hidden = false;

    var label = document.createElement('label');
    label.className = 'pp-fact-modal__label';
    label.setAttribute('for', 'pp-fact-payment-form');
    label.textContent = I18N.payment_label || 'Forma de pago';
    paymentEl.appendChild(label);

    var sel = document.createElement('select');
    sel.id = 'pp-fact-payment-form';
    sel.className = 'pp-fact-modal__select';
    var forms = I18N.payment_forms || { '01': '01', '03': '03', '04': '04', '28': '28' };
    ['01', '03', '04', '28'].forEach(function (code) {
      var opt = document.createElement('option');
      opt.value = code; opt.textContent = forms[code] || code;
      if (code === (o.suggested_payment_form || '03')) opt.selected = true;
      sel.appendChild(opt);
    });
    paymentEl.appendChild(sel);

    var hint = document.createElement('small');
    hint.className = 'pp-fact-modal__hint';
    hint.textContent = I18N.payment_hint || '';
    paymentEl.appendChild(hint);
  }

  // ── Perfiles fiscales ──────────────────────────────────────────
  function showProfilesLoading() {
    if (profilesErrorEl) profilesErrorEl.hidden = true;
    if (noprofileEl) noprofileEl.hidden = true;
    if (profilesSection) profilesSection.hidden = false;
    clearChildren(profilesEl);
    var d = document.createElement('div');
    d.className = 'pp-fact-modal__profiles-loading';
    var sp = document.createElement('span');
    sp.className = 'pp-fact-modal__spinner pp-fact-modal__spinner--sm';
    sp.setAttribute('aria-hidden', 'true');
    d.appendChild(sp);
    d.appendChild(document.createTextNode(I18N.profiles_loading || 'Cargando tus datos fiscales…'));
    profilesEl.appendChild(d);
  }

  function loadProfiles() {
    // GET al App Proxy autenticado: resuelve la cuenta del cliente por su identidad de
    // Shopify (shopify_customer_id → cuenta CRM), NO por correo, y devuelve los perfiles
    // de SU cuenta. Shape: { found, profiles: [...] } (no { ok, data }).
    //
    // El banner "Necesitas Tus Datos Fiscales" SOLO aparece cuando el proxy
    // CONFIRMÓ cero perfiles (2xx con profiles:[]). Error de red / 5xx / shape
    // inesperado = estado de error con Reintentar, nunca el banner (#3383).
    showProfilesLoading();
    callProxy('customer-fiscal', 'GET').then(function (result) {
      if (!result || result.ok === false || !Array.isArray(result.profiles)) {
        renderProfilesError();
        return;
      }
      if (!result.profiles.length) { renderNoProfile(); return; }
      renderProfiles(result.profiles);
    });
  }

  function renderProfilesError() {
    if (profilesSection) profilesSection.hidden = true;
    if (noprofileEl) noprofileEl.hidden = true;
    if (profilesErrorEl) profilesErrorEl.hidden = false;
    selectedProfile = null;
    updateEmit();
  }

  function renderProfiles(profiles) {
    if (noprofileEl) noprofileEl.hidden = true;
    if (profilesErrorEl) profilesErrorEl.hidden = true;
    if (profilesSection) profilesSection.hidden = false;
    clearChildren(profilesEl);

    var preselect = profiles.find(function (p) { return p.is_default; }) || profiles[0];

    profiles.forEach(function (p) {
      var label = document.createElement('label');
      label.className = 'pp-fact-modal__profile';

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'pp-fact-profile';
      input.value = String(p.id);
      input.className = 'pp-fact-modal__profile-radio';
      if (p === preselect) input.checked = true;
      input.addEventListener('change', function () {
        selectedProfile = p;
        updateEmit();
        hideError();
      });

      var main = document.createElement('span');
      main.className = 'pp-fact-modal__profile-main';
      main.textContent = p.legal_name || p.rfc || '';

      var sub = document.createElement('span');
      sub.className = 'pp-fact-modal__profile-sub';
      sub.textContent = (p.rfc || '') + (p.is_default ? ' · ' + (I18N.default_tag || 'Predeterminado') : '');

      label.appendChild(input);
      var txt = document.createElement('span');
      txt.className = 'pp-fact-modal__profile-txt';
      txt.appendChild(main); txt.appendChild(sub);
      label.appendChild(txt);
      profilesEl.appendChild(label);
    });

    // Preselección ⇒ emitir se activa en cuanto el lookup también esté OK
    selectedProfile = preselect;
    updateEmit();
  }

  function renderNoProfile() {
    if (profilesSection) profilesSection.hidden = true;
    if (profilesErrorEl) profilesErrorEl.hidden = true;
    if (noprofileEl) noprofileEl.hidden = false;
    selectedProfile = null;
    updateEmit();
  }

  // ── Emitir ─────────────────────────────────────────────────────
  function handleEmit() {
    hideError();
    if (!order || !selectedProfile) {
      showError(I18N.error_generic);
      return;
    }
    var payload = {
      order_id: order.order_id,
      profile_id: selectedProfile.id,
      total: order.total,
      customer_email: selectedProfile.email_cfdi || window.PP_CUSTOMER_EMAIL || undefined
    };
    var pfSel = document.getElementById('pp-fact-payment-form');
    if (order.needs_payment_form && pfSel) payload.payment_form = pfSel.value;

    emitBtn.disabled = true;
    emitBtn.textContent = I18N.emitting || 'Generando…';

    // POST al App Proxy autenticado: valida orden ∈ cliente/cuenta + perfil ∈ cuenta
    // (a nivel de cuenta, por identidad de Shopify) y delega la emisión al orquestador
    // facturacion-publica/emitir. Reenvía { ok, data|error, code } tal cual.
    callProxy('customer-factura', 'POST', payload).then(function (result) {
      if (!result.ok) {
        emitBtn.disabled = false;
        emitBtn.textContent = I18N.emit || 'Emitir factura';
        showError(result.error || I18N.error_generic);
        return;
      }
      renderSuccess(result.data || {}, payload.customer_email);
    });
  }

  // ── Éxito ──────────────────────────────────────────────────────
  function successLinks(facturaId) {
    var wrap = document.createElement('div');
    wrap.className = 'pp-fact-modal__success-links';
    if (!facturaId) return wrap;
    var btnPdf = document.createElement('button');
    btnPdf.type = 'button';
    btnPdf.className = 'pp-fact-modal__btn pp-fact-modal__btn--primary';
    btnPdf.textContent = I18N.download_pdf || 'Descargar PDF';
    btnPdf.addEventListener('click', function () { downloadInvoice(facturaId, 'pdf'); });
    var btnXml = document.createElement('button');
    btnXml.type = 'button';
    btnXml.className = 'pp-fact-modal__btn pp-fact-modal__btn--ghost';
    btnXml.textContent = I18N.download_xml || 'Descargar XML';
    btnXml.addEventListener('click', function () { downloadInvoice(facturaId, 'xml'); });
    wrap.appendChild(btnPdf); wrap.appendChild(btnXml);
    return wrap;
  }

  function renderSuccess(data, emailTo) {
    clearChildren(successEl);
    var h = document.createElement('h3');
    h.className = 'pp-fact-modal__success-title';
    h.textContent = I18N.success_title || '¡Factura generada!';
    successEl.appendChild(h);

    var p = document.createElement('p');
    p.className = 'pp-fact-modal__success-body';
    var body = (I18N.success_body || 'Te enviamos el PDF y XML a {email}.').replace('{email}', emailTo || '');
    p.textContent = body + ' ' + (I18N.folio_label || 'Folio') + ' ' + (data.folio || '—') + '.';
    successEl.appendChild(p);

    successEl.appendChild(successLinks(data.factura_id));

    var doneWrap = document.createElement('div');
    doneWrap.className = 'pp-fact-modal__success-actions';
    var doneBtn = document.createElement('button');
    doneBtn.type = 'button';
    doneBtn.className = 'pp-fact-modal__btn pp-fact-modal__btn--ghost';
    doneBtn.textContent = I18N.done || 'Listo';
    doneBtn.addEventListener('click', function () { closeModal(true); });
    doneWrap.appendChild(doneBtn);
    successEl.appendChild(doneWrap);

    setPhase('success');
    // Marca visual en la fila para feedback inmediato (se recalcula al recargar).
    if (lastTrigger) {
      var row = lastTrigger.closest('[data-pp-por-facturar-row], .pp-por-facturar__row');
      if (row) row.setAttribute('data-pp-factura-emitida', '');
    }
  }

  function renderAlreadyInvoiced(factura) {
    clearChildren(successEl);
    var h = document.createElement('h3');
    h.className = 'pp-fact-modal__success-title';
    h.textContent = I18N.already_title || 'Esta orden ya tiene factura';
    successEl.appendChild(h);
    if (factura) {
      var p = document.createElement('p');
      p.className = 'pp-fact-modal__success-body';
      p.textContent = (I18N.already_body || 'Ya se emitió el CFDI.') + ' ' + (I18N.folio_label || 'Folio') + ' ' + (factura.folio || '—') + '.';
      successEl.appendChild(p);
      successEl.appendChild(successLinks(factura.id));
    }
    setPhase('success');
  }

  // ── Cerrar ─────────────────────────────────────────────────────
  function closeModal() {
    openSeq++; // mata retries/timeouts pendientes del lookup
    modal.close();
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
  }

  // ── Wire ───────────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-pp-fact-open]');
    if (openBtn) {
      e.preventDefault();
      var num = openBtn.getAttribute('data-order-number') || '';
      var cents = parseInt(openBtn.getAttribute('data-order-total-cents') || '0', 10);
      openModal(num, cents / 100, openBtn);
      return;
    }
    if (e.target.closest('[data-pp-fact-close]')) { e.preventDefault(); closeModal(); return; }
    if (e.target.closest('[data-pp-fact-profiles-retry]')) { e.preventDefault(); loadProfiles(); return; }
    if (e.target.closest('[data-pp-fact-add-profile]')) {
      e.preventDefault();
      closeModal();
      var addBtn = document.querySelector('[data-pp-fiscal-add]');
      if (addBtn) addBtn.click();
      return;
    }
  });

  if (emitBtn) emitBtn.addEventListener('click', handleEmit);

  // Click en el backdrop del <dialog> cierra (el <dialog> nativo ya maneja ESC).
  modal.addEventListener('click', function (e) {
    if (e.target === modal) closeModal();
  });
})();
